import type { FieldGroup, FieldType, FormField, FormSchema } from '@/types/formSchema';
import { isUnknownDateValue } from '@/utils/date';

/**
 * Parse workflow UI schema into FormSchema structure
 * @param uiInput - The UI input schema from workflow
 * @returns FormSchema with parsed groups and fields
 */
export const parseWorkflowUISchema = (uiInput: any): FormSchema => {
  if (!uiInput || !uiInput.groups || !Array.isArray(uiInput.groups)) {
    return { groups: [] };
  }

  return {
    groups: parseFieldGroups(uiInput.groups),
  };
};

/**
 * Parse UI schema groups from a specific schema type (input/output)
 * @param uiSchema - The complete UI schema object
 * @param type - The schema type to parse ('input' or 'output')
 * @returns Array of FieldGroup
 */
export const parseUIGroups = (uiSchema: any, type: 'input' | 'output' = 'input'): FieldGroup[] => {
  const schema = uiSchema?.[type];
  if (!schema?.groups || !Array.isArray(schema.groups)) {
    return [];
  }

  return parseFieldGroups(schema.groups);
};

/**
 * Check if field type supports nested fields
 */
const hasNestedFields = (type: string): boolean => type === 'array' || type === 'object';

/**
 * Add array-specific properties to parsed field
 */
const addArrayProperties = (parsedField: FormField, field: any): void => {
  if (field.item_label) {
    parsedField.item_label = field.item_label;
  }
  if (field.item_identifier_field) {
    parsedField.item_identifier_field = field.item_identifier_field;
  }
};

/**
 * Apply auto-calculation metadata from raw field to parsed FormField.
 * API format: flat camelCase (autoCalculates: "field_id", inferredFromField: "...", inferredType: "...")
 */
const applyRequiredWhenMetadata = (parsedField: FormField, field: any): void => {
  if (field.requiredWhenField) parsedField.requiredWhenField = field.requiredWhenField;
  if (field.requiredWhenValue) parsedField.requiredWhenValue = field.requiredWhenValue;
  if (Array.isArray(field.requiredWhenValues))
    parsedField.requiredWhenValues = field.requiredWhenValues;
};

const applyMirrorMetadata = (parsedField: FormField, field: any): void => {
  if (field.mirrorFieldId) parsedField.mirrorFieldId = field.mirrorFieldId;
  if (field.mirrorWhenField) parsedField.mirrorWhenField = field.mirrorWhenField;
  if (field.mirrorWhenValue) parsedField.mirrorWhenValue = field.mirrorWhenValue;
  if (Array.isArray(field.mirrorWhenValues)) parsedField.mirrorWhenValues = field.mirrorWhenValues;
};

const applyHideWhenMetadata = (parsedField: FormField, field: any): void => {
  if (field.hideWhenField) parsedField.hideWhenField = field.hideWhenField;
  if (field.hideWhenValue) parsedField.hideWhenValue = field.hideWhenValue;
  if (Array.isArray(field.hideWhenValues)) parsedField.hideWhenValues = field.hideWhenValues;
};

const applyConditionalMetadata = (parsedField: FormField, field: any): void => {
  applyRequiredWhenMetadata(parsedField, field);
  applyMirrorMetadata(parsedField, field);
  applyHideWhenMetadata(parsedField, field);
};

const applyAutoCalcMetadata = (parsedField: FormField, field: any): void => {
  if (typeof field.autoCalculates === 'string') {
    parsedField.autoCalculates = { field: field.autoCalculates };
  }
  if (field.inferredFromField) {
    parsedField.inferredFrom = {
      field: field.inferredFromField,
      type: field.inferredType || 'initials',
    };
  }
  if (field.hidden) {
    parsedField.hidden = true;
  }
  if (field.captureAtCreate) {
    parsedField.captureAtCreate = true;
  }
  applyConditionalMetadata(parsedField, field);
};

/**
 * Parse a single field from raw schema data
 * Recursively handles nested fields for array/object types
 */
const parseField = (field: any): FormField => {
  const parsedField: FormField = {
    id: field.id,
    label: field.label || field.id,
    type: field.type || 'text',
    placeholder: field.placeholder,
    defaultValue: field.defaultValue,
    validation: field.validation || {},
    options: field.options,
    description: field.description,
    disabled: field.disabled || false,
    allowUnknown: field.allowUnknown || false,
    // File-field constraints (supported formats with per-type size caps, max
    // file count) — drives multi-file selection and validation in FileField.
    fileMetadata: field.fileMetadata,
  };

  applyAutoCalcMetadata(parsedField, field);

  // Marks the field that titles its enclosing array element. NOTE: this belongs
  // here and NOT in addArrayProperties() — the flag sits on an array's child
  // leaves (e.g. `code`), never on the array field itself, so gating it on
  // `type === 'array'` would silently drop it and the renderer would fall back
  // to guessing the title from field names.
  if (field.isHeader) {
    parsedField.isHeader = true;
  }

  // For array/object types, recursively parse nested fields
  if (hasNestedFields(field.type) && field.fields) {
    parsedField.fields = field.fields.map(parseField);
  }

  // Preserve array-specific properties
  if (field.type === 'array') {
    addArrayProperties(parsedField, field);
  }

  return parsedField;
};

/**
 * After parsing, populate autoCalculates.type and autoCalculates.label on source fields
 * by reading inferredFrom on the corresponding target fields.
 */
const resolveAutoCalcTypes = (groups: FieldGroup[]): void => {
  // Build map: target field id → { type, label }
  const inferredMap = new Map<string, { type: string; label: string }>();
  for (const group of groups) {
    for (const field of group.fields) {
      if (field.inferredFrom) {
        inferredMap.set(field.id, { type: field.inferredFrom.type, label: field.label });
      }
    }
  }
  // Stamp type+label onto source fields that autoCalculate a target
  for (const group of groups) {
    for (const field of group.fields) {
      if (field.autoCalculates && !field.autoCalculates.type) {
        const meta = inferredMap.get(field.autoCalculates.field);
        if (meta) {
          field.autoCalculates.type = meta.type;
          field.autoCalculates.label = meta.label;
        }
      }
    }
  }
};

/**
 * Parse field groups from raw schema data
 * @param groups - Raw groups array from schema
 * @returns Parsed FieldGroup array
 */
export const parseFieldGroups = (groups: any[]): FieldGroup[] => {
  const parsed = groups.map((group: any) => ({
    label: group.label || '',
    fields: (group.fields || []).map(parseField),
  }));
  resolveAutoCalcTypes(parsed);
  return parsed;
};

// Validation helper functions
const validateRequired = (field: FormField, value: any): string | undefined => {
  if (
    field.validation?.required &&
    (value === '' ||
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0))
  ) {
    return `${field.label} is required`;
  }
  return undefined;
};

const validatePattern = (field: FormField, value: any): string | undefined => {
  // The unknown-date sentinel is a valid value for allow_unknown date/dob fields
  // and must bypass any date-format pattern the schema declares.
  if (field.allowUnknown && isUnknownDateValue(value)) {
    return undefined;
  }
  if (field.validation?.pattern && value) {
    const pattern = new RegExp(field.validation.pattern);
    if (!pattern.test(String(value))) {
      // Use placeholder as example if available
      if (field.placeholder) {
        return `${field.label} format is invalid. Example: ${field.placeholder}`;
      }
      return `${field.label} format is invalid`;
    }
  }
  return undefined;
};

const validateMinLength = (field: FormField, stringValue: string): string | undefined => {
  if (
    stringValue &&
    field.validation?.minLength &&
    stringValue.length < field.validation.minLength
  ) {
    return `${field.label} must be at least ${field.validation.minLength} characters`;
  }
  return undefined;
};

const validateMaxLength = (field: FormField, stringValue: string): string | undefined => {
  if (
    stringValue &&
    field.validation?.maxLength &&
    stringValue.length > field.validation.maxLength
  ) {
    return `${field.label} must be at most ${field.validation.maxLength} characters`;
  }
  return undefined;
};

const validateStringLength = (field: FormField, value: any): string | undefined => {
  if (typeof value === 'string' || (value && typeof value.toString === 'function')) {
    const stringValue = String(value);
    return validateMinLength(field, stringValue) || validateMaxLength(field, stringValue);
  }
  return undefined;
};

const validateMinValue = (field: FormField, value: any): string | undefined => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const numValue = Number(value);
  if (!isNaN(numValue) && field.validation?.min !== undefined && numValue < field.validation.min) {
    return `${field.label} must be at least ${field.validation.min}`;
  }
  return undefined;
};

const validateMaxValue = (field: FormField, value: any): string | undefined => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const numValue = Number(value);
  if (!isNaN(numValue) && field.validation?.max !== undefined && numValue > field.validation.max) {
    return `${field.label} must be at most ${field.validation.max}`;
  }
  return undefined;
};

/**
 * Validate a form field value against its validation rules
 * @param field - The FormField containing validation rules
 * @param value - The value to validate
 * @returns Error message if validation fails, undefined if valid
 */
// Scalar option-backed field types whose value must be one of the declared
// options. multi_select is deliberately excluded (legacy values predating the
// current option list must still submit — mirrors the PQC agent's relaxation),
// as is payor (its options are replaced with a live list at render time).
const OPTION_ENFORCED_TYPES: FieldType[] = ['select', 'radio'];

const validateOptionMembership = (field: FormField, value: any): string | undefined => {
  if (!OPTION_ENFORCED_TYPES.includes(field.type)) {
    return undefined;
  }
  // Emptiness is the required check's concern, not membership's
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  if (!field.options || field.options.length === 0) {
    return undefined;
  }

  // Loose comparison: option values may be string/number/boolean while stored
  // values (AI-extracted, imported) are typically strings
  const isMember = field.options.some(option => String(option.value) === String(value));
  return isMember ? undefined : `${field.label} must be one of the available options`;
};

export const validateField = (field: FormField, value: any): string | undefined => {
  // Run all validations and return first error found
  const validators = [
    () => validateRequired(field, value),
    () => validateOptionMembership(field, value),
    () => validatePattern(field, value),
    () => validateStringLength(field, value),
    () => validateMinValue(field, value),
    () => validateMaxValue(field, value),
    () => field.validation?.custom?.(value),
  ];

  for (const validator of validators) {
    const error = validator();
    if (error) {
      return error;
    }
  }

  return undefined;
};

/**
 * Get the default value for a field based on its type
 */
export const getDefaultValueForField = (field: FormField): any => {
  // Use explicit defaultValue if provided
  if (field.defaultValue !== undefined) {
    if (field.defaultValue === 'today') {
      const today = new Date();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      return `${mm}-${dd}-${today.getFullYear()}`;
    }
    return field.defaultValue;
  }

  // Type-based defaults
  switch (field.type) {
    case 'array':
      return [];
    case 'object':
      return {};
    case 'checkbox':
      return false;
    case 'number':
    case 'float':
      // Non-populated numeric fields must default to null, never "" — an
      // empty string is not a valid int/float for the schema and fails
      // downstream call input validation.
      return null;
    default:
      return '';
  }
};

/**
 * Construct an empty data object from UI schema groups
 * @param groups - Array of FieldGroup from UI schema
 * @returns Object with default values for all fields
 */
export const constructEmptyDataFromGroups = (groups: FieldGroup[]): Record<string, any> => {
  const data: Record<string, any> = {};

  groups.forEach(group => {
    group.fields.forEach(field => {
      data[field.id] = getDefaultValueForField(field);
    });
  });

  return data;
};

/**
 * Coerce a single value to its declared schema field type.
 *
 * Numeric coercion (number/float) converts string inputs to numbers; NaN
 * results fall back to the original value so we never corrupt unparseable
 * input. Multi-select coercion lifts a legacy single-string value into a
 * one-element array so cases written when the field was scalar get
 * normalized on the next save (the widget already renders strings
 * tolerantly, but the value never reaches the DB as an array until this
 * coercion runs). Other types pass through. Empty / null / undefined are
 * preserved so cleared inputs don't become `[]` or NaN — only populated
 * legacy values get rewritten.
 */
export const coerceValueToFieldType = (value: any, field?: FormField): any => {
  if (value === '' || value === null || value === undefined) return value;
  if (!field) return value;
  if (field.type === 'number' || field.type === 'float') {
    const n = Number(value);
    return Number.isNaN(n) ? value : n;
  }
  if (field.type === 'multi_select' && typeof value === 'string') {
    return [value];
  }
  return value;
};

const buildFieldIndex = (fields: FormField[]): Map<string, FormField> => {
  const index = new Map<string, FormField>();
  fields.forEach(f => index.set(f.id, f));
  return index;
};

/**
 * Coerce form data to its declared schema types.
 *
 * Walks the provided field groups, coerces values whose key matches a field,
 * and recurses into nested `object` and `array` fields. Keys not described by
 * the schema pass through unchanged — `initializeFormData` intentionally
 * preserves fields outside the UI schema, and we must not drop them.
 */
export const coerceFormDataToSchema = (
  formData: Record<string, any>,
  groups: FieldGroup[],
): Record<string, any> => {
  const allFields: FormField[] = [];
  groups.forEach(g => g.fields.forEach(f => allFields.push(f)));
  return coerceObjectAgainstFields(formData, allFields);
};

const coerceObjectAgainstFields = (
  obj: Record<string, any>,
  fields: FormField[],
): Record<string, any> => {
  const index = buildFieldIndex(fields);
  const out: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const field = index.get(key);
    out[key] = coerceValueAgainstField(value, field);
  }
  return out;
};

const coerceValueAgainstField = (value: any, field?: FormField): any => {
  if (!field) return value;

  if (
    field.type === 'object' &&
    field.fields &&
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
  ) {
    return coerceObjectAgainstFields(value as Record<string, any>, field.fields);
  }

  if (field.type === 'array' && field.fields && Array.isArray(value)) {
    return value.map(item =>
      item && typeof item === 'object' && !Array.isArray(item)
        ? coerceObjectAgainstFields(item as Record<string, any>, field.fields!)
        : item,
    );
  }

  return coerceValueToFieldType(value, field);
};
